// This component as well as the dependency nav-button.tsx are minimally-viable stripped down versions of the original from
// the web-components library which were then heavily modified for our purposes.
// The code in this file should not be used to guide future code style/composition.

import { Box, SxProps, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import {
  generatePath,
  Link,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import styled from 'styled-components'
import { ChevronDownIcon, ChevronUpIcon } from '~components/icons'
import useBreakpoint from '~hooks/use-breakpoint'
import { usePoolName } from '~hooks/use-pool-name'
import { usePoolSubPages } from '~hooks/use-pool-sub-pages'
import { COLORS } from '~theme/colors'
import NavButton from './nav-button'

const StyledSubPageLinksBox = styled.div`
  height: 55px;
  display: flex;
  align-items: center;
`

const HeaderItems = styled.div`
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const StyledSingleLineHeader = styled.div`
  margin: auto;
  column-gap: 16px;
`

const StyledMultilineHeader = styled(StyledSingleLineHeader)`
  min-height: 55px;
  height: 100%;
  margin-bottom: 24px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  column-gap: 16px;
`

function SubPageLink({
  title,
  variant = 'primary',
  mobile = false,
  index,
  poolsLength,
}: {
  title: string
  variant?: 'primary' | 'secondary'
  mobile?: boolean
  index: number
  poolsLength: number
}) {
  const navigate = useNavigate()
  const { pool } = useParams()
  const { pathname } = useLocation()
  const urlEncodedPool = pool ? encodeURIComponent(pool) : ''
  const isActive = title.toLowerCase() === pool?.toLowerCase()

  const link = generatePath(pathname.replace(urlEncodedPool, ':pool'), {
    pool: title.toLowerCase(),
  })

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        pl: index === 0 && !mobile ? '24px !important' : '12px !important',
        pr:
          index === poolsLength - 1 && !mobile
            ? '24px !important'
            : '12px !important',
      }}
      onClick={() => navigate(link)}
    >
      <Link
        to={link}
        style={{
          marginBottom: mobile ? '11px' : '16px',
          marginTop: mobile ? '11px' : '16px',
          textDecoration: 'none',
        }}
      >
        <NavButton isSecondary={variant !== 'primary'} isSelected={isActive}>
          {title}
        </NavButton>
      </Link>
    </Box>
  )
}

const breakpointsWithInlinePoolSelector = ['xxxl', 'xxxxl', 'xxxxxl']
export const PoolSelector = ({
  dropdownOnly = false,
  variant = 'primary',
  sx,
}: {
  dropdownOnly?: boolean
  variant?: 'primary' | 'secondary'
  sx?: SxProps
}) => {
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false)
  const { isMobileSize, breakpoint } = useBreakpoint()
  const poolSubPages = usePoolSubPages()
  const poolName = usePoolName()

  const [scrollY, setScrollY] = useState(0)

  const dropdownRef = useRef<HTMLDivElement | null>(null)

  if (
    poolSubPages.length > 3 &&
    !breakpointsWithInlinePoolSelector.includes(breakpoint)
  ) {
    dropdownOnly = true
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setMobileDropdownOpen(false)
      }
    }

    if (mobileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [mobileDropdownOpen])

  useEffect(() => {
    const handleYPos = () => {
      setScrollY(window.scrollY)
    }
    window.addEventListener('scroll', handleYPos)
    return () => {
      window.removeEventListener('scroll', handleYPos)
    }
  }, [])

  return (
    <Box
      className="tour-step-pool-selector"
      sx={{
        my: 2,
        zIndex: 10,
        padding: 0,
        borderRadius: '8px',
        backgroundColor:
          variant === 'primary'
            ? COLORS.primary.white
            : 'rgba(28, 77, 93, 0.49)',
        backdropFilter: 'blur(40px)',
        '* > div': {
          backgroundColor: 'transparent',
          padding: 0,
        },
        div: { marginBottom: 0 },
        '* > button': {
          color:
            variant === 'primary'
              ? `${COLORS.secondary.darkBlue} !important`
              : 'white !important',
        },
        '* > button > span': {
          backgroundColor: variant === 'primary' ? 'none' : 'white !important',
        },
        ...sx,
      }}
    >
      {isMobileSize || dropdownOnly ? (
        <Box
          ref={dropdownRef}
          sx={{
            width: '160px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative',
            backgroundColor: mobileDropdownOpen
              ? 'rgba(255, 255, 255, 0.15)'
              : scrollY > 10
                ? 'rgba(255, 255, 255, 0.10)'
                : 'transparent',
            transition: 'background-color 0.3s ease',
            borderRadius: '8px',
          }}
          onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              height: '36px',
              padding: '0 16px !important',
            }}
          >
            <Typography
              variant="link"
              sx={{
                color:
                  variant === 'primary'
                    ? COLORS.secondary.darkBlue
                    : COLORS.primary.white,
                fontFamily: 'MaerskHeadlineLight',
                fontSize: '0.75rem',
                letterSpacing: '1.2px',
                textTransform: 'uppercase',
              }}
            >
              {poolName}
            </Typography>
            {mobileDropdownOpen ? (
              <ChevronUpIcon
                style={{ marginTop: '4px' }}
                size={24}
                color={
                  variant === 'primary'
                    ? COLORS.secondary.darkBlue
                    : COLORS.primary.white
                }
              />
            ) : (
              <ChevronDownIcon
                style={{ marginTop: '4px' }}
                size={24}
                color={
                  variant === 'primary'
                    ? COLORS.secondary.darkBlue
                    : COLORS.primary.white
                }
              />
            )}
          </Box>
          {mobileDropdownOpen && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'absolute',
                top: '115%',
                right: 0,
                borderRadius: '8px',
                backgroundColor:
                  variant === 'primary'
                    ? `${COLORS.primary.white} !important`
                    : 'rgba(42, 118, 143, 1) !important',
                width: '160px',
                py: '10px !important',
                boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2)',
              }}
            >
              {poolSubPages?.map((props, i) => (
                <SubPageLink
                  poolsLength={poolSubPages.length}
                  index={i}
                  mobile
                  key={props.title}
                  title={props.title}
                />
              ))}
            </Box>
          )}
        </Box>
      ) : (
        <StyledMultilineHeader>
          <HeaderItems>
            <StyledSubPageLinksBox>
              {poolSubPages?.map((props, i) => (
                <SubPageLink
                  poolsLength={poolSubPages.length}
                  index={i}
                  key={props.title}
                  title={props.title}
                />
              ))}
            </StyledSubPageLinksBox>
          </HeaderItems>
        </StyledMultilineHeader>
      )}
    </Box>
  )
}
